import { useCallback, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";

import { styles } from "./form.js";
import { useFormik } from "formik";
import { EnSchema } from "./en_schema.js";
import { ArSchema } from "./ar_schema.js";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useI18n } from "@/i18n/I18nProvider";
import { arFont, enFont } from "@/styles/font";
import { getContactUaeText } from "../../../locales/contact-uae";

// Lazy-load heavy parts (better LCP / less JS upfront)
const UAE_Files = dynamic(() => import("../UAE_Files.js"), {
  ssr: false,
  loading: () => <Box sx={{ width: "100%", minHeight: 220 }} />,
});

const Terms_and_condation_form = dynamic(
  () => import("../terms-and-condation-form.js"),
  {
    ssr: false,
    loading: () => <Box sx={{ width: "100%", minHeight: 60 }} />,
  }
);

const CIcon = dynamic(() => import("@coreui/icons-react").then((m) => m.default), {
  ssr: false,
  loading: () => <Box sx={{ width: 20, height: 20, display: "inline-block" }} />,
});

export default function Form({ color }) {
  const { t, locale } = useI18n();
  const contactUaeText = getContactUaeText(locale);

  // Product used ONLY to drive schema + conditional sections
  const [selectedProduct, setSelectedProduct] = useState("default");

  // Refs to avoid rerender on file url changes (INP/perf)
  const passportUrlRef = useRef(null);
  const workPermitRef = useRef(null);
  const hrLetterRef = useRef(null);
  const bankStatementRef = useRef(null);

  const setPassportUrl = useCallback((v) => {
    passportUrlRef.current = v;
  }, []);
  const setWorkPermit = useCallback((v) => {
    workPermitRef.current = v;
  }, []);
  const sethrLetter = useCallback((v) => {
    hrLetterRef.current = v;
  }, []);
  const setbankStatement = useCallback((v) => {
    bankStatementRef.current = v;
  }, []);

  const [agreeTerms, setAgreeTerms] = useState(false);
  const [upload, setUpload] = useState(false);
  const [clearFileNames, setClearFileNames] = useState(false);

  // UAE country is fixed here (index 1)
  const uae = useMemo(() => t?.country?.[1] || {}, [t]);
  const countryName = uae.country_name || "UAE";
  const phoneCode = uae.phoneCode || "+971";
  const phoneIcon = uae.icon;

  const initialValues = useMemo(
    () => ({
      name: "",
      mobilePhone: "",
      email: "",
      nationalId: "",
      product: "",
      category: "",
      amount: "",
      mortgageType: "",
      message: "",
      EgyptAddress: "",
      preferredCallingTime: "",
      // keep these for UAE_Files/form submission if needed
      passportUrl: "",
      workPermitUrl: "",
      bankStatementUrl: "",
      hrLetter: "",
    }),
    []
  );

  const validationSchema = useMemo(() => {
    return locale === "en" ? EnSchema(selectedProduct) : ArSchema(selectedProduct);
  }, [locale, selectedProduct]);

  const helperTextSx = useMemo(
    () => (styles.helperText),
    []
  );

  const onSubmit = useCallback(
    async (values, actions) => {
      // Load submit code only on submit (better LCP)
      const { submitForm } = await import("./submit_form.js");

      await submitForm(
        values,
        actions,
        t,
        passportUrlRef.current,
        workPermitRef.current,
        bankStatementRef.current,
        hrLetterRef.current,
        setPassportUrl,
        setWorkPermit,
        sethrLetter,
        setbankStatement,
        setClearFileNames
      );
    },
    [t, setPassportUrl, setWorkPermit, sethrLetter, setbankStatement]
  );

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    setFieldError,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit,

    // INP optimization: don't validate on every keystroke
    validateOnChange: false,
    validateOnBlur: true,
  });

  const handleProductChange = useCallback(
    (event) => {
      const newProduct = event.target.value;

      setSelectedProduct(newProduct);
      // validate after product change (important)
      setFieldValue("product", newProduct, true);

      // Reset dependent fields to avoid stale values + extra validation work
      if (!["auto", "trucks"].includes(newProduct)) {
        setFieldValue("category", "", false);
        setFieldValue("amount", "", false);
      }
      if (!(newProduct === "mortgage" || newProduct === "تمويل عقاري")) {
        setFieldValue("mortgageType", "", false);
      }
    },
    [setFieldValue]
  );

  const handleMobileChange = useCallback(
    (event) => {
      // UAE mobile: digits only, max 9
      const digits = event.target.value.replace(/\D/g, "").slice(0, 9);
      setFieldValue("mobilePhone", digits, false);
    },
    [setFieldValue]
  );

  const handleAmountChange = useCallback(
    (event) => {
      const digits = event.target.value.replace(/\D/g, "");
      setFieldValue("amount", digits, false);
    },
    [setFieldValue]
  );

  const menuProps300 = useMemo(
    () => ({
      PaperProps: { style: { marginTop: 1, maxHeight: 300 } },
    }),
    []
  );

  const menuProps250 = useMemo(
    () => ({
      PaperProps: { style: { marginTop: 1, maxHeight: 250 } },
    }),
    []
  );

  return (
    <Box
      className="horizontal-safe-padding vertical-safe-padding"
      sx={{ backgroundColor: "#FAFAFA" }}
    >
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        {/* Header */}
        <Box sx={{ textAlign: "center" }}>
          <Box sx={{ position: "relative", display: "inline-block" }}>
            <Box
              component="h3"
              sx={{
                color: "#2D317F",
                fontSize: { xs: "24px", sm: "32px" },
                fontWeight: "bold",
                m: 0,
              }}
            >
              {contactUaeText.form.title}
            </Box>
          </Box>

          <Box
            sx={{
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: "38px",
              color: "#4B5563",
              listStylePosition: "inside",
              mt: 2,
            }}
          >
            {contactUaeText.form.subTitle1}
          </Box>

          <Box
            sx={{
              fontSize: "18px",
              fontWeight: 500,
              lineHeight: "38px",
              color: "#F18222",
            }}
          >
            {contactUaeText.form.subTitle2}
          </Box>
        </Box>

        {/* Form */}
        <Box
          component="form"
          sx={{ width: { xs: "100%", sm: "488px" }, marginTop: 2 }}
          onSubmit={handleSubmit}
          autoComplete="off"
          noValidate
        >
          {/* full name */}
          <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
            <InputLabel htmlFor="name" sx={styles.label}>
              {contactUaeText.form.name}
            </InputLabel>

            <TextField
              value={values.name}
              onChange={handleChange}
              id="name"
              name="name"
              type="text"
              autoComplete="name"
              onBlur={handleBlur}
              error={Boolean(touched.name && errors.name)}
              sx={styles.input}
              InputProps={{ sx: { height: "45px" } }}
              variant="outlined"
            />

            <Box sx={helperTextSx}>{touched.name && errors.name}</Box>
          </Box>

          {/* phone number */}
          <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
            <InputLabel sx={styles.label} htmlFor="mobilePhone">
              {contactUaeText.form.phone_number}
            </InputLabel>

            <TextField
              value={values.mobilePhone}
              onChange={handleMobileChange}
              id="mobilePhone"
              name="mobilePhone"
              type="text"
              inputProps={{
                inputMode: "numeric",
                pattern: "[0-9]*",
                maxLength: 9,
              }}
              onBlur={handleBlur}
              error={Boolean(touched.mobilePhone && errors.mobilePhone)}
              sx={{ ...styles.input, pl: 0 }}
              placeholder={locale === "en" ? "UAE" : "الإمارات"}
              InputProps={{
                startAdornment: (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box sx={{ width: 30, height: 30, display: "inline-flex" }}>
                      <CIcon
                        icon={phoneIcon}
                        style={{
                          width: "20px",
                          marginLeft: locale === "ar" ? "5px" : 0,
                          marginRight: locale === "en" ? "5px" : "8px",
                        }}
                      />
                    </Box>

                    <Typography
                      component="span"
                      sx={{ fontWeight: 500, fontSize: "16px" }}
                    >
                      {phoneCode}
                    </Typography>

                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        marginRight: locale === "ar" ? 2 : "8px",
                        marginLeft: locale === "en" ? 2 : 0,
                        height: "45px",
                        borderColor: "gray",
                      }}
                    />
                  </Box>
                ),
                sx: { height: "45px" },
              }}
            />

            <Box sx={helperTextSx}>
              {touched.mobilePhone && errors.mobilePhone}
            </Box>
          </Box>

          {/* Disclaimer */}
          <Box
            sx={{
              backgroundColor: "#FFF2E6",
              borderRadius: "8px",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              width: "100%",
              gap: "8px",
              mb: "15px",
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#FF8300" }} />
            <Typography variant="body2" sx={{ color: "#F18222" }}>
              {t.form_labels.Disclaimer}
            </Typography>
          </Box>

          {/* email */}
          <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
            <InputLabel sx={styles.label} htmlFor="email">
              {contactUaeText.form.email}
            </InputLabel>

            <TextField
              value={values.email}
              onChange={handleChange}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              onBlur={handleBlur}
              error={Boolean(touched.email && errors.email)}
              sx={styles.input}
              InputProps={{ sx: { height: "45px" } }}
              variant="outlined"
            />

            <Box sx={helperTextSx}>{touched.email && errors.email}</Box>
          </Box>

          {/* product */}
          <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
            <InputLabel sx={styles.label} htmlFor="product">
              {contactUaeText.form.product}
            </InputLabel>

            <TextField
              name="product"
              value={values.product || "default"}
              onChange={handleProductChange}
              id="product"
              onBlur={handleBlur}
              select
              error={Boolean(touched.product && errors.product)}
              sx={styles.input}
              InputProps={{
                sx: {
                  height: "45px",
                  color: values.product ? "#505050" : "grey",
                  lineHeight: "2",
                },
              }}
              SelectProps={{ MenuProps: menuProps300 }}
              variant="outlined"
            >
              <MenuItem
                disabled
                value="default"
                className={locale === "en" ? enFont.className : arFont.className}
                sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
              >
                {contactUaeText.form.product_list_name}
              </MenuItem>

              {contactUaeText.form.product_list.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.value}
                  className={
                    locale === "en" ? enFont.className : arFont.className
                  }
                  sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
                >
                  {option.product_name}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={helperTextSx}>{touched.product && errors.product}</Box>
          </Box>

          {/* auto/trucks extra fields */}
          {(selectedProduct === "auto" || selectedProduct === "trucks") && (
            <>
              <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
                <InputLabel sx={styles.label}>{t.form_labels.car_type}</InputLabel>

                <TextField
                  value={values.category || "default"}
                  onChange={handleChange}
                  name="category"
                  type="text"
                  onBlur={handleBlur}
                  error={Boolean(touched.category && errors.category)}
                  sx={styles.input}
                  select
                  style={{ height: "45px" }}
                  InputProps={{
                    sx: {
                      height: "45px",
                      color: values.category ? "#505050" : "grey",
                      lineHeight: "2",
                    },
                  }}
                  SelectProps={{ MenuProps: menuProps250 }}
                >
                  <MenuItem
                    disabled
                    value="default"
                    className={
                      locale === "en" ? enFont.className : arFont.className
                    }
                    sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
                  >
                    {t.form_labels.car_placeholder}
                  </MenuItem>

                  {t.car_type.map((option) => (
                    <MenuItem
                      key={option.id}
                      value={option.value}
                      className={
                        locale === "en" ? enFont.className : arFont.className
                      }
                      sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
                    >
                      {option.value}
                    </MenuItem>
                  ))}
                </TextField>

                <Box sx={helperTextSx}>{touched.category && errors.category}</Box>
              </Box>

              <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
                <InputLabel htmlFor="amount" sx={styles.label}>
                  {t.form_labels.car_price}
                </InputLabel>

                <TextField
                  value={values.amount}
                  onChange={handleAmountChange}
                  id="amount"
                  name="amount"
                  type="text"
                  inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                  onBlur={handleBlur}
                  error={Boolean(touched.amount && errors.amount)}
                  sx={styles.input}
                  InputProps={{ sx: { height: "45px" } }}
                  variant="outlined"
                />

                <Box sx={helperTextSx}>{touched.amount && errors.amount}</Box>
              </Box>
            </>
          )}

          {/* mortgage extra field */}
          {(selectedProduct === "mortgage" || selectedProduct === "تمويل عقاري") && (
            <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
              <InputLabel sx={styles.label}>{t.form_labels.mortgage_type}</InputLabel>

              <TextField
                value={values.mortgageType || "default"}
                onChange={handleChange}
                name="mortgageType"
                type="text"
                onBlur={handleBlur}
                error={Boolean(touched.mortgageType && errors.mortgageType)}
                sx={styles.input}
                select
                style={{ height: "45px" }}
                InputProps={{
                  sx: {
                    height: "45px",
                    color: values.mortgageType ? "#505050" : "grey",
                    lineHeight: "2",
                  },
                }}
              >
                <MenuItem
                  disabled
                  value="default"
                  className={
                    locale === "en" ? enFont.className : arFont.className
                  }
                  sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
                >
                  {t.form_labels.mortgage_type}
                </MenuItem>

                {t.mortgage_type.map((option) => (
                  <MenuItem
                    key={option.id}
                    value={option.value}
                    className={
                      locale === "en" ? enFont.className : arFont.className
                    }
                    sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
                  >
                    {option.value}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={helperTextSx}>
                {touched.mortgageType && errors.mortgageType}
              </Box>
            </Box>
          )}

          {/* UAE Files (lazy chunk) */}
          <UAE_Files
            styles={styles}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            touched={touched}
            setFieldError={setFieldError}
            errors={errors}
            t={t}
            phone={values.mobilePhone}
            values={values}
            setPassportUrl={setPassportUrl}
            setWorkPermit={setWorkPermit}
            sethrLetter={sethrLetter}
            setbankStatement={setbankStatement}
            clearFileNames={clearFileNames}
            setUpload={setUpload}
          />

          {/* Egypt Address */}
          <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
            <InputLabel htmlFor="EgyptAddress" sx={styles.label}>
              {t.form_labels.EgyptAddress}
            </InputLabel>

            <TextField
              value={values.EgyptAddress}
              onChange={handleChange}
              id="EgyptAddress"
              name="EgyptAddress"
              type="text"
              onBlur={handleBlur}
              error={Boolean(touched.EgyptAddress && errors.EgyptAddress)}
              sx={styles.input}
              InputProps={{ sx: { height: "45px" } }}
              variant="outlined"
            />

            <Box sx={helperTextSx}>
              {touched.EgyptAddress && errors.EgyptAddress}
            </Box>
          </Box>

          {/* Preferred calling time */}
          <Box sx={styles.inputWrapper} style={{ width: "100%" }}>
            <InputLabel sx={styles.label} htmlFor="preferredCallingTime">
              {t.form_labels.preferredCallingTime}
            </InputLabel>

            <TextField
              value={values.preferredCallingTime || "default"}
              onChange={handleChange}
              name="preferredCallingTime"
              id="preferredCallingTime"
              type="text"
              onBlur={handleBlur}
              error={Boolean(
                touched.preferredCallingTime && errors.preferredCallingTime
              )}
              sx={styles.input}
              select
              style={{ height: "45px" }}
              InputProps={{
                sx: { height: "45px", color: "grey", lineHeight: "2" },
              }}
              SelectProps={{ MenuProps: menuProps250 }}
            >
              <MenuItem
                disabled
                value="default"
                className={locale === "en" ? enFont.className : arFont.className}
                sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
              >
                {t.form_labels.time_placholder}
              </MenuItem>

              {t.preferredCallingTime.map((time, i) => (
                <MenuItem
                  key={i}
                  value={time}
                  className={
                    locale === "en" ? enFont.className : arFont.className
                  }
                  sx={{ direction: locale === "ar" ? "rtl" : "ltr" }}
                >
                  {time}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={helperTextSx}>
              {touched.preferredCallingTime && errors.preferredCallingTime}
            </Box>
          </Box>

          {/* Terms (lazy chunk) */}
          <Box sx={{ mb: 1 }}>
            <Terms_and_condation_form
              agreeTerms={agreeTerms}
              setAgreeTerms={setAgreeTerms}
            />
          </Box>

          <Button
            disabled={Boolean(isSubmitting) || !agreeTerms || !upload}
            type="submit"
            aria-label="Form submit button"
            sx={styles.btn}
            style={{
              backgroundColor: "#2D317F",
              border: "0px solid #74357D",
              padding: "12px 24px",
              // ثابت لتقليل CLS لما يظهر الـ spinner
              minHeight: 48,
            }}
          >
            {isSubmitting ? (
              <CircularProgress style={{ color: "#fff" }} size={25} />
            ) : (
              contactUaeText.form.submit
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
